import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import SearchBar from '../common/SearchBar';
import { useAllianceSearchStore } from '../../stores/freelancerSearchStore';
import { useRef, useState } from 'react';
import FreelancerCard from '../freelancers/FreelancerCard';
import { FreelancerSearchResult } from '../../types/search';
import { LoadingSpinner } from '../LoadingSpinner';
import AllianceRequestModal from './AllianceRequestModal';

interface RequestAllianceProps {
  userIdsToIgnore: string[];
}

const RequestAlliance = ({ userIdsToIgnore }: RequestAllianceProps) => {
  const { t } = useTranslation('alliances');
  const {
    results,
    freelancerSearchingLoading: loading,
    error,
    hasMore,
    suggestions,
    suggestionsLoading,
    search_freelancers,
    fetch_more_freelancers,
    fetch_suggestions,
  } = useAllianceSearchStore();

  const [search, setSearch] = useState('');
  const [selectedFreelancer, setSelectedFreelancer] = useState<FreelancerSearchResult | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  const freelancers = results.filter(result => !userIdsToIgnore.includes(result.id));

  const handleSearchSubmit = async (searchTerm: string) => {
    const trimmed = searchTerm.trim();
    if (trimmed.length < 3) return;

    setSearch(trimmed);
    await search_freelancers({
      keyword: trimmed,
      limit_count: 10,
    });

    setHasSearched(true);

    if (searchResultsRef.current) {
      searchResultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="rounded-2xl border-2 border-gray-300 bg-white p-4 shadow-xl md:w-[35rem] md:p-6">
      <h2 className="mb-2 text-lg font-semibold text-secondary md:mb-4 md:text-xl">
        {t('requestAlliance')}
      </h2>

      <SearchBar
        value={search}
        placeholder={t('searchPlaceholder', { ns: 'alliances' })}
        rounded={false}
        suggestions={suggestions}
        onChange={setSearch}
        onSelect={handleSearchSubmit}
        onSubmit={() => handleSearchSubmit(search)}
        onFetchSuggestions={fetch_suggestions}
        submitDisabled={search.trim().length < 3}
        loading={suggestionsLoading}
      />

      <div
        className="mt-4 divide-y divide-[#E1E9F0] rounded-2xl bg-white pb-4"
        ref={searchResultsRef}
      >
        {freelancers.map(freelancer => (
          <FreelancerCard
            className="pl-0 mt-4 mb-4 md:items-start md:mt-0 md:mb-0 md:pt-4 md:pb-4 md:pl-0 md:pr-0"
            key={freelancer.id}
            freelancer={freelancer}
            complete={false}
            showEyeIcon={true}
            onClick={() => setSelectedFreelancer(freelancer)}
          />
        ))}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {error && !loading && (
          <div className="mt-4 flex flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50 px-4 py-12">
            <p className="mb-2 font-semibold text-red-700">
              {t('searchErrorMessage', { ns: 'freelancers' })}
            </p>
            <button
              className="mt-2 rounded bg-red-600 px-4 py-2 text-white transition hover:bg-red-700"
              onClick={() => handleSearchSubmit(search)}
            >
              {t('retry', { ns: 'freelancers' })}
            </button>
            <p className="mt-2 text-xs text-red-500">
              {t('contactSupport', { ns: 'freelancers' })}
            </p>
          </div>
        )}

        {freelancers.length === 0 && !loading && !error && !!search && hasSearched && (
          <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
            <p className="mb-2 text-lg font-semibold text-gray-500">
              {t('noResults', { ns: 'freelancers' })}
            </p>
            <p className="text-sm text-gray-400">
              {t('noFreelancersDetails', { ns: 'alliances' })}
            </p>
          </div>
        )}

        {hasMore && !loading && (
          <div>
            <Button
              className="my-4 w-full md:w-auto md:justify-self-end md:px-8"
              onClick={() => fetch_more_freelancers()}
            >
              {t('loadMore', { ns: 'freelancers' })}
            </Button>
          </div>
        )}
      </div>

      {selectedFreelancer && (
        <AllianceRequestModal
          freelancer={selectedFreelancer}
          onClose={() => setSelectedFreelancer(null)}
          onAllianceRequested={() => handleSearchSubmit(search)}
        />
      )}
    </div>
  );
};

export default RequestAlliance;
